/** @jsxImportSource @emotion/react */
// React/core
import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react'
// Third-party
import { css } from '@emotion/react'
import { Spin, App, DatePicker } from 'antd'
import {
  LoadingOutlined,
  EnvironmentOutlined,
  PlusOutlined,
  DeleteOutlined,
  DownloadOutlined,
} from '@ant-design/icons'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)
// Internal (@sportninja/common)
import colors from '@sportninja/common/constants/appColors'
import { font, weightMedium } from 'src/components/css'
import req from '@sportninja/common/api/request'
// Relative
import Icon from 'src/components/Icon'
import LocationModal from 'src/components/LocationModal'
import VenuePickerModal from './components/VenuePickerModal'
import { VenueCard } from './components/VenueCard'
import Divider from './components/Divider'
import type { Venue } from './hooks/useSchedulerLocations'
import type {
  TimeSlot,
  CreateTimeSlotData,
  UpdateTimeSlotData,
} from './hooks/useSchedulerTimeSlots'
import type { Competition } from './hooks/useSchedulerCompetition'
import type { SchedulerFlight } from './hooks/useSchedulerFlights'
import ConcreteSlotImportModal from './components/ConcreteSlotImportModal'
import FlightSwitcherDropdown from './components/FlightSwitcherDropdown'
import TimeSlotsListView from './components/TimeSlotsListView'

interface ConfigurePageProps {
  competitionId: string
  flightId?: string | null
  flightStartDate?: string | null
  flightEndDate?: string | null
  onFlightDatesChanged?: () => void
  competition: Competition | null
  refetchCompetition: () => Promise<void>
  venues: Venue[]
  locationsLoading: boolean
  locationsError: Error | null
  refetchLocations: () => Promise<void>
  attachVenues: (venueIds: string[]) => Promise<void>
  detachVenue: (venueId: string) => Promise<void>
  timeSlots: TimeSlot[]
  timeSlotCounts: Record<string, number>
  timeSlotsLoading: boolean
  createTimeSlot: (
    facilityId: string,
    data: CreateTimeSlotData
  ) => Promise<TimeSlot>
  updateTimeSlot: (
    timeSlotId: string,
    data: UpdateTimeSlotData
  ) => Promise<TimeSlot>
  deleteTimeSlot: (timeSlotId: string) => Promise<void>
  duplicateTimeSlot: (
    timeSlotId: string,
    targetFacilityId?: string
  ) => Promise<TimeSlot>
  getTimeSlotsForFacility: (facilityId: string) => TimeSlot[]
  createTimeSlotForVenue: (
    facilityIds: string[],
    data: CreateTimeSlotData
  ) => Promise<void>
  updateTimeSlotBatch: (
    timeSlotIds: string[],
    data: UpdateTimeSlotData
  ) => Promise<void>
  deleteTimeSlotBatch: (timeSlotIds: string[]) => Promise<void>
  deleteAllImported: () => Promise<void>
  deleteAllForFlight: () => Promise<void>
  refetchTimeSlots: () => Promise<void>
  divisions: { id: string; name: string }[]
  // Flight switcher props for combined layout
  flights: SchedulerFlight[]
  currentFlightId: string | null
  flightsLoading: boolean
  onDeleteFlight?: (flightId: string) => Promise<void>
  onFlightSelect?: (flight: SchedulerFlight) => void
  onNewFlight?: () => void
}

/**
 * Configure Page Component
 */
const ConfigurePage: React.FC<ConfigurePageProps> = ({
  competitionId,
  flightId,
  flightStartDate,
  flightEndDate,
  onFlightDatesChanged,
  competition,
  refetchCompetition,
  venues,
  locationsLoading,
  locationsError,
  refetchLocations,
  attachVenues,
  detachVenue,
  timeSlots,
  timeSlotCounts,
  timeSlotsLoading,
  createTimeSlot,
  updateTimeSlot,
  deleteTimeSlot,
  duplicateTimeSlot,
  getTimeSlotsForFacility,
  createTimeSlotForVenue,
  updateTimeSlotBatch,
  deleteTimeSlotBatch,
  deleteAllImported,
  deleteAllForFlight,
  refetchTimeSlots,
  divisions,
  flights,
  currentFlightId,
  flightsLoading,
  onDeleteFlight,
  onFlightSelect,
  onNewFlight,
}) => {
  const { message } = App.useApp()

  const organizationId = competition?.organization_id

  // Location modal state
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false)
  const [selectedVenue, setSelectedVenue] = useState<Venue | null>(null)
  const [isVenuePickerOpen, setIsVenuePickerOpen] = useState(false)
  // Track whether the location modal was opened from the picker
  const [locationModalFromPicker, setLocationModalFromPicker] = useState(false)
  // Import modal state
  const [isImportModalOpen, setIsImportModalOpen] = useState(false)
  // Ref for concrete slots section refetch
  const concreteSlotsRefetchRef = useRef<(() => Promise<void>) | null>(null)

  const [isClearingSlots, setIsClearingSlots] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const [viewMode, setViewMode] = useState<'locations' | 'list'>('locations')

  // Handle add location — opens the venue picker
  const handleAddLocation = useCallback(() => {
    setIsVenuePickerOpen(true)
  }, [])

  // Handle clearing all time slots (and concrete slots) for the current flight
  const handleClearAllTimeSlots = useCallback(async () => {
    if (
      // eslint-disable-next-line no-alert
      !window.confirm(
        'Clear all time slots and imported game slots? This cannot be undone.'
      )
    ) {
      return
    }
    try {
      setIsClearingSlots(true)
      // Clear time slots
      if (flightId) {
        await deleteAllForFlight()
      } else {
        await deleteAllImported()
      }
      // Clear concrete slots
      const deleteUrl = flightId
        ? `/schedules/${competitionId}/concrete-slots?flight_id=${flightId}`
        : `/schedules/${competitionId}/concrete-slots`
      await req(deleteUrl, { method: 'DELETE' })
      // Refetch concrete slots section
      concreteSlotsRefetchRef.current?.()
      message.success('All time slots cleared')
    } catch {
      message.error('Failed to clear time slots')
    } finally {
      setIsClearingSlots(false)
    }
  }, [flightId, competitionId, deleteAllForFlight, deleteAllImported, message])

  // Handle exporting time slots as CSV
  const handleExportTimeSlots = useCallback(async () => {
    setIsExporting(true)
    try {
      const flightParam = flightId ? `?flight_id=${flightId}` : ''
      const response = await req(
        `/schedules/${competitionId}/time-slots/export/csv${flightParam}`,
        { method: 'GET', parseJSON: false }
      )
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'time_slots_export.csv'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
    } catch {
      message.error('Failed to export time slots')
    } finally {
      setIsExporting(false)
    }
  }, [competitionId, flightId, message])

  // Handle edit location
  const handleEditVenue = useCallback((venue: Venue) => {
    setSelectedVenue(venue)
    setIsLocationModalOpen(true)
  }, [])

  // Handle remove venue (detach from scheduler selection)
  const handleDeleteVenue = useCallback(
    async (venue: Venue) => {
      if (
        // eslint-disable-next-line no-alert
        !window.confirm(
          `Remove "${venue.name}" from this competition? Time slots for this venue will also be removed.`
        )
      ) {
        return
      }

      try {
        await detachVenue(venue.id)
        message.success('Venue removed')
      } catch (err) {
        console.error('Failed to remove venue:', err)
        message.error('Failed to remove venue. Please try again.')
      }
    },
    [detachVenue, message]
  )

  // Handle location modal close
  const handleLocationModalClose = useCallback(() => {
    setIsLocationModalOpen(false)
    setSelectedVenue(null)
  }, [])

  // Handle location modal success
  const handleLocationModalSuccess = useCallback(async () => {
    setIsLocationModalOpen(false)
    setSelectedVenue(null)
    setLocationModalFromPicker(false)
    refetchLocations()
  }, [refetchLocations])

  // Handle "Create New Location" from inside the picker
  const handleCreateNewFromPicker = useCallback(() => {
    setIsVenuePickerOpen(false)
    setSelectedVenue(null)
    setLocationModalFromPicker(true)
    setIsLocationModalOpen(true)
  }, [])

  // Handle venues attached from the picker
  const handleVenuesAttached = useCallback(
    async (venueIds: string[]) => {
      await attachVenues(venueIds)
    },
    [attachVenues]
  )

  // Prepare initial state for location modal edit mode
  const initialVenueData = useMemo(() => {
    if (!selectedVenue) {
      return undefined
    }
    return {
      id: selectedVenue.id,
      name: selectedVenue.name || selectedVenue.name_full || '',
      address_id: selectedVenue.address?.id,
      address_line1: selectedVenue.address?.street_1 || '',
      address_line2: selectedVenue.address?.street_2 || '',
      city: selectedVenue.address?.city || '',
      postcode: selectedVenue.address?.postal_code || '',
      country_id: selectedVenue.address?.country?.id
        ? Number(selectedVenue.address.country.id)
        : undefined,
      province_id: selectedVenue.address?.province?.id
        ? Number(selectedVenue.address.province.id)
        : undefined,
      country: selectedVenue.address?.country?.iso_3166_2 || '',
      province: selectedVenue.address?.province?.iso_3166_2 || '',
    }
  }, [selectedVenue])

  // Game Schedule Date Range state — flight dates take priority
  // Parse as UTC to avoid timezone shifts (API returns UTC dates like "2026-02-04T00:00:00+00:00")
  const [startDate, setStartDate] = useState<dayjs.Dayjs | null>(() => {
    if (flightStartDate) {
      return dayjs.utc(flightStartDate)
    }
    if (competition?.starts_at) {
      return dayjs.utc(competition.starts_at)
    }
    return null
  })
  const [endDate, setEndDate] = useState<dayjs.Dayjs | null>(() => {
    if (flightEndDate) {
      return dayjs.utc(flightEndDate)
    }
    if (competition?.ends_at) {
      return dayjs.utc(competition.ends_at)
    }
    return null
  })

  // Sync dates when flight or competition data loads
  React.useEffect(() => {
    if (flightStartDate) {
      setStartDate(dayjs.utc(flightStartDate))
    } else if (competition?.starts_at && !startDate) {
      setStartDate(dayjs.utc(competition.starts_at))
    }
    if (flightEndDate) {
      setEndDate(dayjs.utc(flightEndDate))
    } else if (competition?.ends_at && !endDate) {
      setEndDate(dayjs.utc(competition.ends_at))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [competition, flightStartDate, flightEndDate])

  // Auto-save date range to competition when both dates are valid
  const isInitialMount = useRef(true)
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    // Skip the initial mount / sync from competition data
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }

    // Both dates must be defined
    if (!startDate || !endDate) {
      return
    }

    // End date must be on or after start date
    if (endDate.isBefore(startDate, 'day')) {
      return
    }

    const newStartsAt = startDate.format('YYYY-MM-DD')
    const newEndsAt = endDate.format('YYYY-MM-DD')

    // Determine current dates to compare against (flight or competition)
    const currentStartsAt = flightId
      ? flightStartDate || null
      : competition?.starts_at
      ? dayjs.utc(competition.starts_at).format('YYYY-MM-DD')
      : null
    const currentEndsAt = flightId
      ? flightEndDate || null
      : competition?.ends_at
      ? dayjs.utc(competition.ends_at).format('YYYY-MM-DD')
      : null

    if (newStartsAt === currentStartsAt && newEndsAt === currentEndsAt) {
      return
    }

    // Debounce the save
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current)
    }

    debounceTimer.current = setTimeout(async () => {
      try {
        if (flightId) {
          // Save to flight — never modify competition dates
          await req(`/scheduler/flights/${flightId}`, {
            method: 'PUT',
            body: JSON.stringify({
              start_date: newStartsAt,
              end_date: newEndsAt,
            }),
          })
          message.success('Flight date range updated')
          onFlightDatesChanged?.()
        } else {
          // Legacy: save to competition
          await req(`/schedules/${competitionId}`, {
            method: 'PUT',
            body: JSON.stringify({
              starts_at: newStartsAt,
              ends_at: newEndsAt,
            }),
          })
          message.success('Date range updated')
          await refetchCompetition()
        }
      } catch (err) {
        console.error('Failed to update date range:', err)
        message.error('Failed to update date range')
      }
    }, 800)

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate])

  return (
    <div
      css={css`
        min-height: 100%;
      `}
    >
      <div
        css={css`
          max-width: 1440px;
          margin: 0 auto;
        `}
      >
        {/* Schedule Flights Date Range Section */}
        <div
          data-tour-flight-range
          css={css`
            display: flex;
            align-items: center;
            justify-content: space-between;
            width: 100%;
            margin-bottom: 24px;
          `}
        >
          {/* Left side: Label + Flight Switcher */}
          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: 8px;
            `}
          >
            <h2
              css={css`
                margin: 0;
                font-family: 'Inter', sans-serif;
                font-size: 14px;
                font-weight: 600;
                line-height: 16.8px;
                letter-spacing: 1.68px;
                text-transform: uppercase;
                color: ${colors.NEUTRAL_0};
              `}
            >
              Schedule Flights Date Range
            </h2>
            <FlightSwitcherDropdown
              variant='inline'
              flights={flights}
              currentFlightId={currentFlightId}
              loading={flightsLoading}
              onDeleteFlight={onDeleteFlight}
              onFlightSelect={onFlightSelect}
              onNewFlight={onNewFlight}
            />
          </div>

          {/* Right side: Date Pickers */}
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 16px;
              flex-shrink: 0;
            `}
          >
            {/* Start Date */}
            <div
              css={css`
                display: flex;
                flex-direction: column;
              `}
            >
              <span
                css={css`
                  font-family: 'Inter', sans-serif;
                  font-size: 14px;
                  font-weight: 400;
                  line-height: 20.3px;
                  color: ${colors.NEUTRAL_0};
                `}
              >
                Start Date
              </span>
              <DatePicker
                value={startDate}
                onChange={(date) => setStartDate(date)}
                format='MMM DD, YYYY'
                suffixIcon={
                  <Icon
                    name='calendar'
                    faType='far'
                    fontSize={18}
                    color={colors.NEUTRAL_0}
                  />
                }
                allowClear={false}
                css={css`
                  && {
                    background-color: #293241;
                    border: 1px solid #3a4659;
                    border-radius: 8px;
                    height: 40px;
                    padding: 8px 16px;
                    min-width: 170px;

                    .ant-picker-input > input {
                      font-family: 'Inter', sans-serif;
                      font-size: 14px;
                      font-weight: 500;
                      line-height: 20.3px;
                      color: ${colors.NEUTRAL_0};
                    }

                    &:hover,
                    &.ant-picker-focused {
                      border-color: ${colors.PRIMARY};
                    }

                    .ant-picker-suffix {
                      color: ${colors.NEUTRAL_0};
                    }
                  }
                `}
              />
            </div>

            {/* Start End */}
            <div
              css={css`
                display: flex;
                flex-direction: column;
              `}
            >
              <span
                css={css`
                  font-family: 'Inter', sans-serif;
                  font-size: 14px;
                  font-weight: 400;
                  line-height: 20.3px;
                  color: ${colors.NEUTRAL_0};
                `}
              >
                Start End
              </span>
              <DatePicker
                value={endDate}
                onChange={(date) => setEndDate(date)}
                format='MMM DD, YYYY'
                suffixIcon={
                  <Icon
                    name='calendar'
                    faType='far'
                    fontSize={18}
                    color={colors.NEUTRAL_0}
                  />
                }
                allowClear={false}
                css={css`
                  && {
                    background-color: #293241;
                    border: 1px solid #3a4659;
                    border-radius: 8px;
                    height: 40px;
                    padding: 8px 16px;
                    min-width: 170px;

                    .ant-picker-input > input {
                      font-family: 'Inter', sans-serif;
                      font-size: 14px;
                      font-weight: 500;
                      line-height: 20.3px;
                      color: ${colors.NEUTRAL_0};
                    }

                    &:hover,
                    &.ant-picker-focused {
                      border-color: ${colors.PRIMARY};
                    }

                    .ant-picker-suffix {
                      color: ${colors.NEUTRAL_0};
                    }
                  }
                `}
              />
            </div>
          </div>
        </div>

        {/* Divider between Date Range and Locations */}
        <Divider
          css={css`
            margin-bottom: 24px;
          `}
        />

        {/* Locations & Time Slots Section Header */}
        <div
          css={css`
            display: flex;
            align-items: center;
            justify-content: space-between;
            width: 100%;
            margin-bottom: 16px;
          `}
        >
          {/* View Mode Toggle */}
          <div
            css={css`
              display: flex;
              align-items: center;
              border: 1px solid ${colors.SECONDARY_200};
              border-radius: 8px;
              overflow: hidden;
            `}
          >
            <button
              type='button'
              onClick={() => setViewMode('locations')}
              css={css`
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 8px;
                padding: 8px 16px;
                background: ${viewMode === 'locations'
                  ? colors.SECONDARY_300
                  : colors.SECONDARY_500};
                border: none;
                cursor: pointer;
                transition: background 0.2s ease;
              `}
            >
              <Icon
                name='map-marker-alt'
                fontSize={16}
                color={
                  viewMode === 'locations'
                    ? colors.NEUTRAL_0
                    : colors.NEUTRAL_50
                }
              />
              <span
                css={css`
                  font-family: 'Inter', sans-serif;
                  font-size: 14px;
                  font-weight: ${viewMode === 'locations' ? 700 : 400};
                  line-height: 20.3px;
                  color: ${viewMode === 'locations'
                    ? colors.NEUTRAL_0
                    : colors.NEUTRAL_50};
                  white-space: nowrap;
                `}
              >
                Locations
              </span>
            </button>
            <button
              type='button'
              onClick={() => setViewMode('list')}
              css={css`
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 8px;
                padding: 8px 16px;
                background: ${viewMode === 'list'
                  ? colors.SECONDARY_300
                  : colors.SECONDARY_500};
                border: none;
                cursor: pointer;
                transition: background 0.2s ease;
              `}
            >
              <Icon
                name='list'
                fontSize={16}
                color={
                  viewMode === 'list' ? colors.NEUTRAL_0 : colors.NEUTRAL_50
                }
              />
              <span
                css={css`
                  font-family: 'Inter', sans-serif;
                  font-size: 14px;
                  font-weight: ${viewMode === 'list' ? 700 : 400};
                  line-height: 20.3px;
                  color: ${viewMode === 'list'
                    ? colors.NEUTRAL_0
                    : colors.NEUTRAL_50};
                  white-space: nowrap;
                `}
              >
                List View
              </span>
            </button>
          </div>

          {/* Action Buttons */}
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 8px;
            `}
          >
            {/* Add Location */}
            <button
              type='button'
              onClick={handleAddLocation}
              css={css`
                display: flex;
                align-items: center;
                gap: 8px;
                padding: 8px 16px;
                background: ${colors.PRIMARY};
                border: none;
                border-radius: 8px;
                cursor: pointer;
                transition: all 0.2s ease;

                &:hover {
                  background: ${colors.PRIMARY_HOVER};
                }
              `}
            >
              <Icon
                name='map-marker-alt'
                fontSize={24}
                color={colors.PRIMARY_TEXT}
              />
              <span
                css={css`
                  font-family: 'Inter', sans-serif;
                  font-size: 14px;
                  font-weight: 500;
                  line-height: 20.3px;
                  color: ${colors.PRIMARY_TEXT};
                  white-space: nowrap;
                `}
              >
                Select Location(s)
              </span>
            </button>
            {/* Import Time Slots */}
            <button
              type='button'
              onClick={() => setIsImportModalOpen(true)}
              css={css`
                display: flex;
                align-items: center;
                gap: 8px;
                padding: 8px 16px;
                background: ${colors.PRIMARY};
                border: none;
                border-radius: 8px;
                cursor: pointer;
                transition: all 0.2s ease;

                &:hover {
                  background: ${colors.PRIMARY_HOVER};
                }
              `}
            >
              <Icon name='upload' fontSize={24} color={colors.PRIMARY_TEXT} />
              <span
                css={css`
                  font-family: 'Inter', sans-serif;
                  font-size: 14px;
                  font-weight: 500;
                  line-height: 20.3px;
                  color: ${colors.PRIMARY_TEXT};
                  white-space: nowrap;
                `}
              >
                Import Time Slots
              </span>
            </button>
            {/* Download Time Slots */}
            {timeSlots.length > 0 && (
              <button
                type='button'
                onClick={handleExportTimeSlots}
                disabled={isExporting}
                css={css`
                  display: flex;
                  align-items: center;
                  gap: 8px;
                  padding: 8px 16px;
                  background: ${colors.SECONDARY_300};
                  border: 1px solid ${colors.SECONDARY_100};
                  border-radius: 8px;
                  cursor: pointer;
                  transition: all 0.2s ease;
                  opacity: ${isExporting ? 0.6 : 1};

                  &:hover {
                    background: ${colors.SECONDARY_200};
                  }

                  &:disabled {
                    cursor: not-allowed;
                  }
                `}
              >
                <DownloadOutlined
                  css={css`
                    font-size: 24px;
                    color: ${colors.NEUTRAL_0};
                  `}
                />
                <span
                  css={css`
                    ${font.body}
                    font-size: 14px;
                    ${weightMedium}
                    line-height: 20.3px;
                    color: ${colors.NEUTRAL_0};
                    white-space: nowrap;
                  `}
                >
                  {isExporting ? 'Downloading...' : 'Download Time Slots'}
                </span>
              </button>
            )}
            {/* Clear All Time Slots */}
            {timeSlots.length > 0 && (
              <button
                type='button'
                onClick={handleClearAllTimeSlots}
                disabled={isClearingSlots}
                css={css`
                  display: flex;
                  align-items: center;
                  gap: 8px;
                  padding: 8px 16px;
                  background: transparent;
                  border: 1px solid ${colors.ERROR_200};
                  border-radius: 8px;
                  cursor: pointer;
                  transition: all 0.2s ease;
                  opacity: ${isClearingSlots ? 0.6 : 1};

                  &:hover {
                    background: rgba(223, 66, 66, 0.1);
                  }

                  &:disabled {
                    cursor: not-allowed;
                  }
                `}
              >
                <DeleteOutlined
                  css={css`
                    font-size: 24px;
                    color: ${colors.ERROR_200};
                  `}
                />
                <span
                  css={css`
                    font-family: 'Inter', sans-serif;
                    font-size: 14px;
                    font-weight: 500;
                    line-height: 20.3px;
                    color: ${colors.ERROR_200};
                    white-space: nowrap;
                  `}
                >
                  {isClearingSlots ? 'Clearing...' : 'Clear All Time Slots'}
                </span>
              </button>
            )}
          </div>
        </div>

        {/* Content Area */}
        {viewMode === 'list' ? (
          <TimeSlotsListView
            venues={venues}
            timeSlots={timeSlots}
            divisions={divisions}
            timeSlotsLoading={timeSlotsLoading}
            onUpdateTimeSlot={updateTimeSlot}
            onDeleteTimeSlot={deleteTimeSlot}
            onDuplicateTimeSlot={duplicateTimeSlot}
          />
        ) : venues.length > 0 ? (
          // Venue cards — show in locations view
          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: 8px;
            `}
          >
            {venues.map((venue) => (
              <div key={venue.id}>
                <VenueCard
                  venue={venue}
                  competitionId={competitionId}
                  timeSlots={timeSlots}
                  timeSlotCounts={timeSlotCounts}
                  timeSlotsLoading={timeSlotsLoading}
                  divisions={divisions}
                  onEditVenue={handleEditVenue}
                  onDeleteVenue={handleDeleteVenue}
                  onAddTimeSlot={createTimeSlot}
                  onUpdateTimeSlot={updateTimeSlot}
                  onDeleteTimeSlot={deleteTimeSlot}
                  onDuplicateTimeSlot={duplicateTimeSlot}
                  getTimeSlotsForFacility={getTimeSlotsForFacility}
                  onAddTimeSlotForVenue={createTimeSlotForVenue}
                  onUpdateTimeSlotBatch={updateTimeSlotBatch}
                  onDeleteTimeSlotBatch={deleteTimeSlotBatch}
                />
              </div>
            ))}
          </div>
        ) : locationsLoading ? (
          // Loading state
          <div
            css={css`
              display: flex;
              align-items: center;
              justify-content: center;
              padding: 48px;
              background-color: ${colors.SECONDARY_400};
              border-radius: 8px;
              border: 1px solid ${colors.SECONDARY_200};
            `}
          >
            <Spin
              indicator={
                <LoadingOutlined
                  css={css`
                    font-size: 24px;
                  `}
                  spin
                />
              }
            />
            <span
              css={css`
                margin-left: 12px;
                color: ${colors.NEUTRAL_50};
                font-family: 'Inter', sans-serif;
                font-size: 14px;
              `}
            >
              Loading locations...
            </span>
          </div>
        ) : locationsError ? (
          // Error state
          <div
            css={css`
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              padding: 48px;
              background-color: ${colors.SECONDARY_400};
              border-radius: 8px;
              border: 1px solid ${colors.ERROR};
            `}
          >
            <span
              css={css`
                color: ${colors.ERROR};
                font-family: 'Inter', sans-serif;
                font-size: 14px;
                margin-bottom: 8px;
              `}
            >
              Failed to load locations
            </span>
            <button
              type='button'
              onClick={() => refetchLocations()}
              css={css`
                background: ${colors.PRIMARY};
                border: none;
                color: ${colors.PRIMARY_TEXT};
                padding: 8px 16px;
                border-radius: 4px;
                cursor: pointer;
                font-family: 'Inter', sans-serif;
                font-size: 14px;

                &:hover {
                  background: ${colors.PRIMARY_HOVER};
                }
              `}
            >
              Retry
            </button>
          </div>
        ) : (
          // Empty state — no venues attached
          <div
            css={css`
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              padding: 48px;
              background-color: ${colors.SECONDARY_400};
              border-radius: 8px;
              border: 1px dashed ${colors.SECONDARY_200};
            `}
          >
            <EnvironmentOutlined
              css={css`
                font-size: 48px;
                color: ${colors.NEUTRAL_100};
                margin-bottom: 16px;
              `}
            />
            <span
              css={css`
                color: ${colors.NEUTRAL_50};
                font-family: 'Inter', sans-serif;
                font-size: 16px;
                font-weight: 500;
                margin-bottom: 8px;
              `}
            >
              No locations selected
            </span>
            <span
              css={css`
                color: ${colors.NEUTRAL_100};
                font-family: 'Inter', sans-serif;
                font-size: 14px;
                margin-bottom: 16px;
                text-align: center;
              `}
            >
              Select locations from your organization&apos;s venues
            </span>
            <button
              type='button'
              onClick={handleAddLocation}
              css={css`
                display: flex;
                align-items: center;
                gap: 8px;
                background: ${colors.PRIMARY};
                border: none;
                color: ${colors.PRIMARY_TEXT};
                padding: 10px 20px;
                border-radius: 4px;
                cursor: pointer;
                font-family: 'Inter', sans-serif;
                font-size: 14px;
                font-weight: 500;

                &:hover {
                  background: ${colors.PRIMARY_HOVER};
                }
              `}
            >
              <PlusOutlined />
              Select Location(s)
            </button>
          </div>
        )}
      </div>

      {/* Venue Picker Modal */}
      {organizationId && (
        <VenuePickerModal
          isOpen={isVenuePickerOpen}
          onClose={() => setIsVenuePickerOpen(false)}
          competitionId={competitionId}
          alreadyAttachedVenueIds={venues.map((v) => v.id)}
          onVenuesAttached={handleVenuesAttached}
          onCreateNew={handleCreateNewFromPicker}
        />
      )}

      {/* Location Modal */}
      {organizationId && (
        <LocationModal
          isOpen={isLocationModalOpen}
          onClose={handleLocationModalClose}
          organizationId={organizationId}
          onSuccess={handleLocationModalSuccess}
          initialState={initialVenueData}
          initialFacilities={selectedVenue?.facilities || []}
          scheduleId={locationModalFromPicker ? competitionId : undefined}
          flightId={locationModalFromPicker ? flightId : undefined}
        />
      )}

      {/* Concrete Slot Import Modal */}
      <ConcreteSlotImportModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        competitionId={competitionId}
        flightId={flightId}
        onImportSuccess={async () => {
          refetchTimeSlots()
          concreteSlotsRefetchRef.current?.()
          refetchLocations()
        }}
      />
    </div>
  )
}

export default ConfigurePage
